/* ASK BRILL'S NEW-CHAT SCREEN (C1 on her canvas, approved 2026-10-20).
 *
 * What she sees before she has asked anything: the next open section, counted
 * from storage (utils/whereLeftOff.js), as one line, and her —
 * the woman with the teal hair, which replaced the stick figure. The box she
 * types in is ChatPanel's own, so there is one composer, not two.
 *
 * Picking a starter only puts words in her box; she still sends them. The one
 * thing that starts Brill is "Pick it up", exactly as pressing that section in
 * the sidebar would.
 */
import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { usePanelMode } from "../hooks/usePanelMode";
import { COACHES } from "../utils/askBrillChats";

/* Things Brill really does, typed into the empty box one after another.
   Each is something she can ask today — never a feature that isn't built. */
export const TYPED_EXAMPLES = [
  "Help me say what I do in one sentence.",
  "Help me write my LinkedIn bio.",
  "Create a masterclass presentation from my offer.",
  "Write my landing page copy.",
];

/** The box's placeholder, typed out a letter at a time while `on`. Stops for reduced motion. */
export function useTypedExamples(on) {
  const [text, setText] = useState("");
  useEffect(() => {
    if (!on) { setText(""); return undefined; }
    const still = typeof window !== "undefined" && window.matchMedia
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (still) { setText(TYPED_EXAMPLES[0]); return undefined; }
    let i = 0; let n = 0; let hold = 0;
    const t = setInterval(() => {
      const full = TYPED_EXAMPLES[i];
      if (n < full.length) { n += 1; setText(full.slice(0, n)); return; }
      hold += 1;
      if (hold < 40) return;
      hold = 0; n = 0; i = (i + 1) % TYPED_EXAMPLES.length;
      setText("");
    }, 55);
    return () => clearInterval(t);
  }, [on]);
  return text;
}

/* HER HAIR (her read, 2026-10-21). The two flowing lines are CLIPPED to the
   hair's outline: the original drawing had other lines around her for them
   to run into, and on their own here, carrying on past the hair's edge, they
   looked like a mistake. In dark mode the hair is ELECTRIC TEAL with the lines
   cut out of it in the page colour — the pale teal it had went muddy at the
   figure's low opacity — so in dark mode she is a little less faint (`fade`)
   and her sweater is the deeper brand teal, or hair and sweater were one
   shape. Salmon was her other choice. Her EYES are their own colour, dark in
   both modes — they used to share the hair's, so they went teal (her ask).
   Dark-mode `fade` came back DOWN from 0.42 to 0.3 on 2026-10-22 (her read:
   the electric hair pushed in on the coach chips) — nearly light mode's 0.28. */
const FIG = {
  light: { teal: "#007D6E", aqua: "#0AEFD3", hair: "#214042", lines: "#0AEFD3", linesOpacity: 0.75, dark: "#27233A", skin: "#F1BFA4", light: "#FFFFFF", glow: 1, fade: 0.28, markBig: "#0AEFD3", eyes: "#214042" },
  dark:  { teal: "#007D6E", aqua: "#0AEFD3", hair: "#0AEFD3", lines: "#0B0D15", linesOpacity: 0.9, dark: "#A8B0B8", skin: "#E8B396", light: "#0B0D15", glow: 0.18, fade: 0.3, markBig: "#007D6E", eyes: "#0B0D15" },
};
/* The logo's leaves (public/brand/brill-mark-*.svg), the big ones with their vein. */
const MARK_BIG = "M0,-0.6 C9.03,-5.99 9.91,-17.26 2.6,-25.1 C-5.75,-17.26 -8.51,-5.99 0,-0.6Z M0.13,-5.01 L1.35,-12.85 L1.56,-21.18 L-0.05,-12.85Z";
const MARK_SMALL = "M0,-0.6 C6.51,-3.9 7.07,-10.8 1.8,-15.6 C-4.19,-10.8 -6.15,-3.9 0,-0.6Z";
const HAIR = "M233.6 1113.8C274.2 1172.6 331.1 1218 401.5 1225.5C471 1233 518.8 1187.5 557.1 1135C610.9 1061.5 659.3 976.3 745.4 936C795.7 912.5 855.1 910.9 903.6 939.6C949.3 966.7 982 1011.1 1012.4 1053.5C1053.1 1110.4 1099.2 1171.4 1167 1188.2C1234.4 1205 1305.2 1172.1 1357.7 1126.7C1410.2 1081.2 1459.9 1031.7 1501.3 975.9C1607 833 1757.1 887.1 1782.5 842.3C1797.9 815.1 1771.7 781 1776.7 755.6C1788.7 694.3 1763.5 667.3 1739.6 655.1C1705.1 637.5 1682.3 650.3 1641.2 642.7C1586.9 632.7 1571.7 607.7 1539.1 589.5C1488.5 561.3 1434.5 574 1377.4 603.4C1317.9 634 1256.9 684.5 1187.5 694.3C1073.7 710.3 981.5 665 885.9 624.4C811.3 592.6 741.1 551 664.6 523.7C518.4 471.5 361.6 515.2 260.8 632.4C219 681 188.5 739.5 174.4 802.1C152.1 901.6 168.7 1008.7 221.9 1095.9C225.7 1102 229.5 1107.9 233.6 1113.8Z";

/* `fade` overrides her opacity for a place that needs her quieter — behind
   the module chats, where she sits under text. */
export function AskBrillFigure({ width = 300, fade }) {
  const { panelMode } = usePanelMode() || {};
  const c = panelMode === "dark" ? FIG.dark : FIG.light;
  return (
    <svg viewBox="80 400 2340 1150" width={width} height={Math.round(width * 1150 / 2340)} role="img"
      aria-label="Line drawing of a woman at her laptop, her long teal hair flowing behind her"
      style={{ display: "block", maxWidth: "100%", height: "auto", pointerEvents: "none", opacity: fade ?? c.fade }}>
      
<defs><clipPath id="askbrill-hair-clip"><path d={HAIR} /></clipPath><radialGradient id="askbrill-fig-glow" cx="0.5" cy="0.5" r="0.5"><stop offset="0" stopColor="#FBD9C4" stopOpacity="0.95" /><stop offset="0.6" stopColor="#FBE3D3" stopOpacity="0.55" /><stop offset="1" stopColor="#FBE3D3" stopOpacity="0" /></radialGradient></defs>
<ellipse opacity={c.glow} cx="1640" cy="1020" rx="820" ry="620" fill="url(#askbrill-fig-glow)" />
<path fill={c.teal} d="M1916.1 1409.7C1902.2 1427.1 1867.5 1442.2 1830.8 1454.1C1830.8 1454 1830.8 1453.8 1830.8 1453.6C1841.4 1326.1 1862.6 1248.2 1878.3 1205.5C1900.7 1281 1932.5 1389.2 1916.1 1409.7ZM1437.7 1409.7C1421.2 1389.2 1453 1281 1475.4 1205.5C1491.2 1248.2 1512.3 1326.1 1522.9 1453.6C1522.9 1453.8 1523 1454 1523 1454.1C1486.3 1442.2 1451.6 1427.1 1437.7 1409.7ZM1921.9 1093.3C1873.8 1048.8 1767.1 1010 1676.9 1010C1586.6 1010 1480 1048.8 1431.9 1093.3C1341 1177.4 1131.2 1536.6 1324.2 1536.6C1418.3 1536.6 1676.9 1536.6 1676.9 1536.6C1676.9 1536.6 1935.4 1536.6 2029.5 1536.6C2222.6 1536.6 2012.8 1177.4 1921.9 1093.3Z" />
<path fill={c.hair} d={HAIR} />
<path clipPath="url(#askbrill-hair-clip)" fill={c.lines} fillOpacity={c.linesOpacity} d="M510.6 910C494.2 910 478.3 908.4 463 905.2C420.6 896.4 385.1 875 360.4 843.3C325.5 798.5 313.5 741.5 326.7 682.6C341.6 616.2 386.6 556.9 444.3 527.8L447 533.2C390.9 561.4 347.1 619.2 332.5 684C319.8 741 331.3 796.3 365.1 839.6C402 886.9 457.3 903.9 510.6 903.9C534.8 903.9 558.6 900.4 580.1 894.6C644.4 877.3 690.6 840.9 736.2 801.9C769.9 773 810.4 741.2 854.8 724.5C902.8 706.4 950.7 718.2 997.1 729.5C1013.8 733.6 1031 737.8 1048 740.5C1106 749.9 1179.8 755.8 1235.1 718.3C1257.8 702.9 1276.2 682.3 1294.1 662.3C1299.1 656.7 1304.3 650.9 1309.6 645.2C1345.1 607 1383.4 580.7 1423.4 567.2C1454.5 556.8 1484.7 555.8 1513.2 564.3L1511.5 570C1484.2 561.9 1455.2 562.9 1425.3 572.9C1386.3 586.1 1348.8 611.8 1314 649.3C1308.8 654.9 1303.6 660.7 1298.5 666.3C1280.5 686.6 1261.8 707.5 1238.5 723.3C1213.4 740.3 1183.1 749.9 1146.1 752.6C1117.7 754.7 1086.2 752.7 1047 746.4C1029.8 743.7 1012.5 739.4 995.7 735.3C950.2 724.2 903.2 712.7 856.9 730.1C813.4 746.5 773.4 777.9 740.1 806.5C694 845.9 647.2 882.7 581.7 900.4C557.8 906.8 533.8 910 510.6 910Z" />
<path fill={c.skin} d="M1715.3 960.7C1697.5 896.2 1717.5 849.6 1717.5 849.6L1676.9 849.6L1636.2 849.6C1636.2 849.6 1656.3 896.2 1638.5 960.7C1624.5 1011.2 1577.1 1024.3 1577.1 1024.3L1676.9 1024.8L1776.7 1024.3C1776.7 1024.3 1729.3 1011.2 1715.3 960.7Z" />
<path fill={c.hair} d="M1656.3 894.5C1656.3 894.5 1679.3 911.6 1707.9 909C1707.8 899.2 1708.5 890.5 1709.5 882.8C1690.3 880.6 1666.6 883.2 1656.3 894.5Z" />
<path fill={c.skin} d="M1675.3 673.8C1619.4 673.8 1594.7 723.4 1598.2 786C1601.6 847.9 1632.7 898.3 1675.3 898.3C1717.9 898.3 1749 847.9 1752.5 786C1755.9 723.4 1731.3 673.8 1675.3 673.8Z" />
<path fill={c.eyes} d="M1651.2 779.5C1642.5 772.2 1628.3 772.2 1619.6 779.5L1615.8 775C1626.6 765.8 1644.2 765.8 1655 775L1651.2 779.5Z" />
<path fill={c.eyes} d="M1732.6 779.5C1723.9 772.2 1709.7 772.2 1701 779.5L1697.2 775C1708 765.8 1725.6 765.8 1736.5 775L1732.6 779.5Z" />
<path fill={c.light} d="M1650.4 837.3C1650.4 851.5 1661.9 863 1676.1 863C1690.3 863 1701.8 851.5 1701.8 837.3L1650.4 837.3Z" />
<path fill={c.skin} d="M1676.9 1010C1638.5 1010 1597.2 1017 1558.7 1028.5C1579.9 1061.5 1624.9 1084.2 1676.9 1084.2C1728.9 1084.2 1773.8 1061.5 1795.1 1028.5C1756.6 1017 1715.2 1010 1676.9 1010Z" />
<path fill={c.light} d="M1676.9 1087.7C1670.3 1087.7 1663.8 1087.4 1657.4 1086.7L1658.1 1079.7C1664.3 1080.4 1670.6 1080.7 1676.9 1080.7C1723.9 1080.7 1767.1 1061.5 1789.5 1030.5L1795.1 1034.6C1771.4 1067.4 1726.1 1087.7 1676.9 1087.7Z" />
<path fill={c.skin} d="M1745.6 787.2C1742.9 797.4 1745.8 807 1752.3 808.8C1758.7 810.5 1766.1 803.6 1768.8 793.4C1771.6 783.2 1768.6 773.6 1762.2 771.8C1755.8 770.1 1748.3 777 1745.6 787.2Z" />
<path fill={c.dark} d="M1637.8 1485.8C1631 1484.6 1470.5 1455.5 1435.3 1411.6C1419 1391.2 1444 1300.8 1473.3 1201.9C1475.8 1193.8 1478 1186.1 1480.1 1179L1485.9 1180.7C1483.8 1187.8 1481.5 1195.5 1479.1 1203.6C1468 1241.2 1454.1 1287.9 1445.2 1327.5C1432.6 1383.5 1435.4 1402.1 1440 1407.8C1473.7 1450 1637.2 1479.6 1638.8 1479.9L1637.8 1485.8Z" />
<path fill={c.dark} d="M1742.5 1480.5L1741.3 1474.6C1784.5 1465.3 1887.8 1440.2 1913.7 1407.8C1918.3 1402.1 1921.1 1383.5 1908.5 1327.5C1899.6 1287.9 1885.8 1241.2 1874.7 1203.6C1872.2 1195.5 1870 1187.8 1867.9 1180.7L1873.6 1179C1875.7 1186.1 1878 1193.8 1880.4 1201.9C1909.7 1300.8 1934.7 1391.2 1918.4 1411.6C1892.3 1444.3 1797 1468.8 1742.5 1480.5Z" />
<path clipPath="url(#askbrill-hair-clip)" fill={c.lines} fillOpacity={c.linesOpacity} d="M462.3 1158.2C438.9 1158.2 415 1155 391.1 1148.6C341.5 1135.2 294.8 1108 259.6 1072.1C225.3 1037 198.3 992.3 181.5 942.7C165.3 894.6 158.9 842.7 162.9 792.5L168.9 793C160.6 895.9 196.1 998.7 263.9 1067.9C336.1 1141.6 449.6 1170.9 540 1139.2C598.7 1118.6 646.4 1075.8 692.4 1034.4C702.4 1025.5 712.6 1016.2 722.8 1007.4C757 977.8 788.2 956.2 818.3 941.4C856.5 922.7 893.7 914.8 928.9 917.9C998.5 924.1 1052.7 971 1105.1 1016.4C1146.7 1052.4 1189.7 1089.6 1240.8 1107.4C1314.4 1133 1400.8 1113.6 1455.7 1059C1489.6 1025.3 1512.1 979.4 1522.5 955L1528 957.3C1517.4 982.1 1494.5 1028.9 1459.9 1063.3C1432.4 1090.7 1396.6 1109.9 1356.5 1118.9C1316.4 1128 1275.7 1125.9 1238.8 1113.1C1186.7 1094.9 1143.2 1057.3 1101.2 1020.9C1047.1 974.1 996.1 929.9 928.4 923.9C866.4 918.4 802.4 946.4 726.7 1011.9C716.6 1020.7 706.4 1029.9 696.4 1038.8C649.9 1080.7 601.8 1123.9 542 1144.9C516.7 1153.8 489.9 1158.2 462.3 1158.2Z" />
<path fill={c.hair} d="M1694.1 669.8C1694.1 669.8 1694.5 697.9 1715.7 727C1732.6 750.2 1755.7 766.8 1755.7 766.8C1755.7 766.8 1767.7 682.8 1694.1 669.8Z" />
<path fill={c.hair} d="M1617.4 873.8C1584.5 852.4 1546.2 795.8 1566.7 727.1C1590 649.1 1690.6 646.9 1699.3 679.8C1700 693.5 1703.9 714.6 1643 757.4C1599 788.3 1597 824.8 1617.4 873.8Z" />
<path fill={c.skin} d="M1775 1536.6C1731.6 1536.6 1606.1 1536.6 1606.1 1536.6C1606.1 1536.6 1618.5 1511.6 1640.7 1504.2C1707.8 1481.7 1821.9 1536.6 1775 1536.6Z" />
<path fill={c.dark} d="M1954 1202.5L1399.8 1202.5C1393 1202.5 1387.8 1208.4 1388.6 1215.1L1425.8 1513.1C1427.5 1526.5 1438.9 1536.6 1452.4 1536.6L1901.4 1536.6C1914.9 1536.6 1926.2 1526.5 1927.9 1513.1L1965.2 1215.1C1966 1208.4 1960.8 1202.5 1954 1202.5Z" />
{/* The Brill mark on her laptop, where the dot was (her idea, 2026-10-21),
    in place of the leaf that stood beside her. Teal big leaves, SALMON small
    ones: the page wanted some, and salmon is the person's mark in Brill. (She
    tried salmon big leaves with white small ones and came back to this.) */}
<g transform="translate(1677 1369) rotate(14) scale(3.1)" fillRule="evenodd">
  <path fill={c.markBig} d={MARK_BIG} />
  <path fill={c.markBig} transform="rotate(180)" d={MARK_BIG} />
  <path fill="#FF5656" transform="rotate(90)" d={MARK_SMALL} />
  <path fill="#FF5656" transform="rotate(270)" d={MARK_SMALL} />
</g>
<rect x="300" y="1532" width="2080" height="7" rx="3.5" fill={c.dark} fillOpacity="0.85" />

    </svg>
  );
}



const chip = (on) => ({
  border: on ? "1.5px solid var(--accent)" : "1px solid var(--border-strong)",
  background: on ? "var(--accent-quiet)" : "transparent",
  color: on ? "var(--accent)" : "var(--text-secondary)",
  borderRadius: 999, padding: "0 16px", minHeight: 40, fontSize: 13.5, fontWeight: on ? 500 : 300,
  cursor: "pointer", fontFamily: "inherit",
});

/* The big, faint Brill mark in the top corner (her mockup) — the logo's own
   four leaves, drawn in the page's quiet accent so it reads in both panels. */
export function BrillMark({ size }) {
  /* The big leaves carry the logo's vein, cut through them (evenodd), as in
     public/brand/brill-mark-*.svg and her mockup. */
  const BIG = MARK_BIG;
  const SMALL = MARK_SMALL;
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="6 6 52 52" style={{ display: "block" }}>
      <g transform="rotate(14 32 32)" fill="var(--accent-quiet)" fillRule="evenodd">
        <path transform="translate(32 32)" d={BIG} />
        <path transform="translate(32 32) rotate(180)" d={BIG} />
        <path transform="translate(32 32) rotate(90)" d={SMALL} />
        <path transform="translate(32 32) rotate(270)" d={SMALL} />
      </g>
    </svg>
  );
}

/* One coach chip. HOVER (her ask, 2026-10-22): the teal outline and text of a
   picked chip without its fill, so hovering says "this one" and never looks
   picked. Focus shows the same, for the keyboard. */
function CoachChip({ label, on, isMobile, onClick }) {
  const [hover, setHover] = useState(false);
  const lit = hover && !on;
  return (
    <button onClick={onClick} aria-pressed={on}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)} onBlur={() => setHover(false)}
      style={{
        ...chip(on),
        /* The WHOLE `border`, never `borderColor`: React removing a longhand
           it set over a shorthand blanks the colour (it went black on
           mouse-out, caught on localhost). */
        ...(lit ? { border: "1px solid var(--accent)", color: "var(--accent)" } : null),
        transition: "border-color 0.15s, color 0.15s, background 0.15s",
        ...(isMobile ? { padding: "0 10px", fontSize: 13 } : null),
      }}>
      {label}
    </button>
  );
}

/* "Get coached" as a little speech bubble with the Brill leaf in it, its tail
   pointing at the chips it labels. The tail sits wholly OUTSIDE the bubble, so
   the translucent fill never doubles where they meet. */
function CoachTag({ point = "right" }) {
  const tail = point === "right"
    ? { right: -6, top: "calc(50% - 5px)", width: 6, height: 10, clipPath: "polygon(0 0, 100% 50%, 0 100%)" }
    : { bottom: -6, left: "calc(50% - 5px)", width: 10, height: 6, clipPath: "polygon(0 0, 100% 0, 50% 100%)" };
  return (
    <span style={{
      position: "relative", display: "inline-flex", alignItems: "center", gap: 5, flexShrink: 0,
      padding: "5px 11px 5px 8px", borderRadius: 999, background: "var(--accent-quiet)",
      color: "var(--accent)", fontSize: 12, fontWeight: 500, whiteSpace: "nowrap", marginRight: point === "right" ? 4 : 0,
    }}>
      <svg aria-hidden="true" width={14} height={14} viewBox="6 6 52 52" style={{ display: "block" }}>
        <g transform="rotate(14 32 32)" fill="currentColor" fillRule="evenodd">
          <path transform="translate(32 32)" d={MARK_BIG} />
          <path transform="translate(32 32) rotate(180)" d={MARK_BIG} />
          <path transform="translate(32 32) rotate(90)" d={MARK_SMALL} />
          <path transform="translate(32 32) rotate(270)" d={MARK_SMALL} />
        </g>
      </svg>
      {/* Side by side, the lighter "Get" before "coached" — she tried them
          stacked and kept the type but not the stack (2026-10-22). */}
      <span><span style={{ fontWeight: 400, opacity: 0.85 }}>Get</span> coached</span>
      <span aria-hidden="true" style={{ position: "absolute", background: "var(--accent-quiet)", ...tail }} />
    </span>
  );
}

/* The module name inside the greeting: underlined at rest, a soft teal wash
   on hover, so it reads as somewhere she can go. */
function ModuleLink({ label, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      style={{
        border: "none", padding: "1px 4px", margin: "0 -4px", borderRadius: 6, cursor: "pointer",
        fontFamily: "inherit", fontSize: "inherit", fontWeight: 500, lineHeight: "inherit",
        color: hover ? "var(--accent)" : "var(--text-primary)",
        background: hover ? "var(--accent-quiet)" : "transparent",
        textDecoration: "underline", textDecorationThickness: 1, textUnderlineOffset: 4,
        textDecorationColor: hover ? "var(--accent)" : "var(--accent-line)",
        transition: "background 0.15s, color 0.15s",
      }}
    >
      {label}
    </button>
  );
}

/* GET COACHED (her call, 2026-10-22), replacing the three chips of 2026-10-20.
   One word per coach after one label — "first person is just long to read".
   Picking one changes the KIND of chat her first message starts, and the box
   shows that coach's first question. Pressing it again goes back to Ask
   Anything, which is also what typing with nothing picked is. The program
   FAQs are facts, not coaching, so they are a quiet link underneath. */

/**
 * leftOff: { last: { id, label } | null, next: { id, label, section, phase } | null }
 * composer: ChatPanel's own box, drawn here in a card — one composer, never two.
 * mode: a key of MODES (utils/askBrillChats.js) — the kind of chat her first message starts.
 */
export function AskBrillHome({
  firstName = "", leftOff = {}, isMobile, onSwitchModule, onStartSection,
  mode = "chat", onMode, onTestimonial, problem = "", composer = null,
}) {
  /* WHERE SHE LEFT OFF (her calls, 2026-10-20). Two ways back, and they do
     different things so they never read as a fork:
       - the MODULE NAME in "Last time you were working in …" is underlined and
         opens that module. Nothing is sent to Brill.
       - "Pick up where you left off", outlined again, starts the next open
         section — the same as pressing it in the sidebar, so it is a message to
         Brill. With nothing left open it just opens the module she was last in.
     "Still to finish" is gone; Pick up is what it used to say. */
  const { last, next } = leftOff || {};
  /* HER SIZE FOLLOWS THE SCREEN (her ask, 2026-10-22: "she doesn't shrink
     until mobile"). 38% of this panel's width, between 240 and 440 — a
     little smaller than she was (her read, 2026-10-22: her hair pushed in on
     the coach chips), full size from a ~1440 laptop up.
     Measured off the panel, not the window, because the sidebar takes its
     share first. The testimonial link moves with her laptop. */
  const boxRef = useRef(null);
  const [boxW, setBoxW] = useState(0);
  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([e]) => setBoxW(Math.round(e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const figW = boxW ? Math.round(Math.max(240, Math.min(440, boxW * 0.38))) : 440;
  /* A narrow panel (a portrait tablet beside the sidebar) stacks the coach
     tag over the chips as a phone does, or the five wrap around it unevenly. */
  const stackCoaches = isMobile || (boxW > 0 && boxW < 640);
  const hello = new Date().getHours() < 12 ? "Good morning" : "Welcome back";
  const pickUp = next
    ? () => onStartSection?.(next.id, next.section)
    : last ? () => onSwitchModule?.(last.id) : null;

  return (
    <div ref={boxRef} style={{ position: "relative", flex: 1, display: "flex", flexDirection: "column", alignItems: "center", padding: isMobile ? "24px 14px 16px" : "24px 24px 24px", textAlign: "center", gap: 18, overflow: "hidden" }}>
      <div style={{ position: "absolute", left: isMobile ? -170 : -260, top: isMobile ? -170 : -250, pointerEvents: "none" }}>
        <BrillMark size={isMobile ? 360 : 620} />
      </div>

      {/* The greeting, box and coaches sit in the MIDDLE, not stuck to the
          top: one spring above, one below. Phone first (her ask), then the
          laptop (her read, 2026-10-22: with the "last time" line gone it sat
          too high). On a laptop the lower spring is twice the upper, so the
          group rides above centre and her hair stays clear of the chips (her
          read, same day: "bump everything up one line"). */}
      <div style={{ flex: 1, minHeight: 0 }} />

      <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center", maxWidth: 560, position: "relative", zIndex: 1 }}>
        <h2 style={{ margin: 0, fontWeight: 300, fontSize: isMobile ? 28 : 40, lineHeight: 1.15, letterSpacing: "-0.4px", color: "var(--text-primary)" }}>
          {hello}{firstName ? <>, <span style={{ color: "var(--accent)", fontWeight: 400 }}>{firstName}</span></> : null}.
        </h2>
        <p style={{ margin: 0, fontSize: isMobile ? 15 : 16.5, fontWeight: 300, lineHeight: 1.55, color: "var(--text-secondary)" }}>
          {last
            ? <>Last time you were working in <ModuleLink label={last.label} onClick={() => onSwitchModule?.(last.id)} />.</>
            : "Start with what's in your head. Ask anything about your business."}
        </p>
      </div>

      {pickUp && (
        <button
          onClick={pickUp}
          style={{
            display: "inline-flex", alignItems: "center", gap: 8, minHeight: 44, padding: "0 22px",
            borderRadius: 999, border: "1.5px solid var(--accent-line)", background: "transparent",
            color: "var(--accent)", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "inherit",
            position: "relative", zIndex: 1,
          }}
        >
          Pick up where you left off <ArrowRight size={15} />
        </button>
      )}

      {composer && (
        <div style={{ width: "100%", maxWidth: 640, display: "flex", flexDirection: "column", gap: 8, position: "relative", zIndex: 1 }}>
          {problem && (
            <p style={{ margin: 0, fontSize: 12.5, color: "var(--warn-text)", textAlign: "left" }}>{problem}</p>
          )}
          <div style={{
            background: "var(--bg-surface)", border: "1px solid var(--border-strong)", borderRadius: 22,
            boxShadow: "var(--shadow-menu)", padding: "10px 10px 10px 16px", textAlign: "left",
          }}>
            {composer}
          </div>
        </div>
      )}

      {/* "Get coached" is a small tag, not a heading (her read, 2026-10-22: the
          cursive she asked for was "too busy"). Beside Mindset on a laptop; on
          a phone the five chips already fill the row, so it sits on top and
          points down at them. "Ask about the program" went the same day — it
          knows nothing about the program (her read, and true: no facts reach it). */}
      <div role="group" aria-label="Get coached" style={{ display: "flex", flexDirection: stackCoaches ? "column" : "row", alignItems: "center", gap: stackCoaches ? 10 : 8, maxWidth: 640, position: "relative", zIndex: 1 }}>
        <CoachTag point={stackCoaches ? "down" : "right"} />
        {/* All five on one row at 375px (measured: 339 of 347px) — tighter on
            a phone, or Social wraps alone. A narrower phone still wraps. */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: isMobile ? 5 : 8, justifyContent: "center" }}>
          {COACHES.map((c) => (
            <CoachChip key={c.mode} label={c.chip} on={mode === c.mode} isMobile={isMobile}
              onClick={() => onMode?.(mode === c.mode ? "chat" : c.mode)} />
          ))}
        </div>
      </div>

      <div style={{ flex: isMobile ? 1 : 2, minHeight: 12 }} />

      {isMobile && (
        /* Her, small, at the foot of the phone screen (her ask: "we need to
           preserve space but she's really cute"), with the testimonial link
           under her laptop as on a laptop screen. */
        /* Far right, bleeding to the screen's edge as on a laptop (her ask).
           The link sits under her LAPTOP (68% across the drawing, 136 of its
           200px), nudged left to 118 so its end clears the screen edge —
           centred under the laptop it ran off by a pixel at 375px. */
        <div style={{ alignSelf: "flex-end", marginRight: -14, width: 200, flexShrink: 0, position: "relative", zIndex: 1 }}>
          <AskBrillFigure width={200} />
          {onTestimonial && (
            <button onClick={onTestimonial} style={{ display: "block", marginLeft: 118, marginTop: 4, transform: "translateX(-50%)", whiteSpace: "nowrap", background: "none", border: "none", padding: 0, color: "var(--text-secondary)", fontSize: 12, textDecoration: "underline", textUnderlineOffset: 3, cursor: "pointer", fontFamily: "inherit", opacity: 0.85 }}>
              Leave a testimonial
            </button>
          )}
        </div>
      )}

      {!isMobile && onTestimonial && (
        /* Under her laptop, centred, quietly (her call, 2026-10-20) — off the
           middle of the screen, which had too many words. The laptop sits 68%
           across the drawing and she hangs 30 past the edge, so its centre is
           32% of her width less 30 from the right (128 at full size) — but
           never under 64, or at her smallest the link runs off the edge. */
        <button onClick={onTestimonial} style={{ position: "absolute", right: Math.max(64, Math.round(figW * 0.32 - 30)), transform: "translateX(50%)", bottom: 8, zIndex: 1, whiteSpace: "nowrap", background: "none", border: "none", padding: 0, color: "var(--text-secondary)", fontSize: 12, textDecoration: "underline", textUnderlineOffset: 3, cursor: "pointer", fontFamily: "inherit", opacity: 0.85 }}>
          Leave a testimonial
        </button>
      )}

      {!isMobile && (
        /* Her, as big and as quiet as the leaf in the opposite corner (her ask):
           a backdrop, not a picture. Behind everything, so the box and the
           buttons always sit on top. Her desk line sits ON the bottom edge of
           the screen (her read: floating above it looked unfinished). */
        <div style={{ position: "absolute", right: -30, bottom: 30, pointerEvents: "none", zIndex: 0 }}>
          <AskBrillFigure width={figW} />
        </div>
      )}
    </div>
  );
}
